import type { FC, MouseEvent } from 'react'

import { Button } from '@skytech/ui/Button'
import { useExpandableText } from '@skytech/utils'
import { sanitizeProductDescription } from '@skytech/utils/html-sanitization'
import HeadphonesIcon from 'mdi-react/HeadphonesIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import type { ProductExtended } from '@/modules/ecom-policy/types'

import { usePolicyFilters } from '../Policy.context'

import Product from './Product'

import './product-group-overview.scss'

const ProductGroupOverview: FC<{
  description: string | null
  groupName: string
  groupedProducts: ProductExtended[]
  groupKey: string
  hasAccessories: boolean
  onModalClose: () => void
}> = ({ description, groupedProducts, groupName, groupKey, hasAccessories, onModalClose }) => {
  const filters = usePolicyFilters()
  const { setProductsWithVisibleAccessoriesIds } = filters
  const [, setCurrentlyBrowsedGroupKey] = filters.currentlyBrowsedGroupKey

  const { textRef, isTextExpandable, isFullTextVisible, onFullTextVisibleSet } =
    useExpandableText(120)

  const handleOnlySelectedProductAccessoriesClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()

    const selectedGroupedProductsIds = groupedProducts.map(({ id }) => id)
    setProductsWithVisibleAccessoriesIds(selectedGroupedProductsIds)
    setCurrentlyBrowsedGroupKey(groupKey)
  }

  return (
    <div className="policy-product-group-overview">
      <h3>{groupName}</h3>

      {description && description.length >= 10 && (
        <p
          ref={textRef}
          className={twJoin(
            'description',
            isFullTextVisible && 'is-full-desc-visible',
            isTextExpandable && 'is-desc-expandable'
          )}
        >
          <div dangerouslySetInnerHTML={{ __html: sanitizeProductDescription(description) }} />
        </p>
      )}
      <div className={twJoin('desc-toggle-wrapper', isTextExpandable && 'is-desc-expandable')}>
        <button onClick={onFullTextVisibleSet}>
          <Translation
            id={isFullTextVisible ? 'Les mindre...' : 'Les mer...'}
            className="lower-case"
          />
        </button>
      </div>

      {hasAccessories && (
        <Button
          variant="primary"
          Icon={<HeadphonesIcon />}
          onClick={handleOnlySelectedProductAccessoriesClick}
        >
          <Translation id="See accessories" />
        </Button>
      )}

      <div className="product-overview-items">
        {groupedProducts.map(product => {
          const { id } = product

          return <Product key={id} hasAccessories={hasAccessories} {...product} />
        })}
      </div>

      <footer>
        <Button variant="tertiary" onClick={onModalClose}>
          <Translation id="Close" />
        </Button>
      </footer>
    </div>
  )
}
export default ProductGroupOverview
